import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FontFamily } from '@/enums/index';
import useBreakpoints from '@/hooks/useBreakpoints';
import { EventSponsor } from '@/resources/EventSponsor';
import { SponsorPackageType } from '@/resources/EventSponsorPackage';
import { BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';

import { SponsorButton } from './SponsorButton';

const Wrapper = styled.div<IThemeProps>`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSans]};
  background: ${({ theme }) => theme.legacy.colors.white};
  color: ${({ theme }) => theme.legacy.colors.black};
`;

const ImageContainer = styled.div<IThemeProps>`
  width: 100%;

  img {
    width: 100%;
    display: block;
    object-fit: contain;
    background: ${({ theme }) => theme.legacy.colors.white};
  }
`;

const wrapperStyles = (cardType) => {
  if (cardType === 'big') {
    return css`
      ${ImageContainer} {
        margin-bottom: 16px;
      }

      @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
        padding: 32px;

        ${ImageContainer} {
          margin-bottom: 32px;
        }
      }
    `;
  }

  if (cardType === 'medium') {
    return css`
      ${ImageContainer} {
        margin-bottom: 16px;
      }

      @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
        padding: 20px;

        ${ImageContainer} {
          margin-bottom: 20px;
        }
      }
    `;
  }

  if (cardType === 'small') {
    return css`
      ${ImageContainer} {
        margin-bottom: 16px;
      }

      @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
        padding: 12px;
      }
    `;
  }
};

interface ISponsorCardProps {
  eventSponsor: EventSponsor;
  cardType: SponsorPackageType;
  onActionClick: () => void;
}

export const SponsorCard: FC<ISponsorCardProps> = ({ eventSponsor, cardType, onActionClick }) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();

  const qaActive = eventSponsor.enabledFeatures.qa && eventSponsor.streamActive;

  const hasAction =
    eventSponsor.enabledFeatures.directLink || eventSponsor.enabledFeatures.about || eventSponsor.enabledFeatures.qa;

  const sponsorButtonTranslationKey = () => {
    if (qaActive) {
      return isMobile || cardType === 'small' ? 'joinShort' : 'join';
    }

    if (eventSponsor.enabledFeatures.about) {
      return 'learnMore';
    }

    return 'website';
  };

  return (
    <Wrapper css={wrapperStyles(cardType)}>
      <ImageContainer>
        <img src={eventSponsor.largeLogoImageUrl} alt={eventSponsor.name} />
      </ImageContainer>

      {hasAction && (
        <SponsorButton
          isExternal={eventSponsor.enabledFeatures.directLink}
          variant={cardType === 'small' ? 'ghost' : 'hollow'}
          text={t(`expo.card.${sponsorButtonTranslationKey()}`)}
          onClick={onActionClick}
        />
      )}
    </Wrapper>
  );
};
